import { supertypeClass, property } from '@haventech/amorphic';
import { Everything } from '@hit/amorphic-types';
import { Policy } from './Policy';
import { PremiumBreakdown } from './PremiumBreakdown';
import { FlatExtraNames, FlatExtraMasks } from '../lib/FlatExtraNames';
import { Utils } from '../Utils';
import { FlatExtra as GQLFlatExtra } from '@hit/underwriting-api-client';
import { PolicyRider } from './PolicyRider';
import { CoverpathUtils } from '../../../coverpath-common/js/static/CoverpathUtils';

@supertypeClass({ toClient: true })
export class FlatExtra extends Everything implements GQLFlatExtra {
  @property()
  name: string;

  @property({
    comment: 'FlatExtra rate. Example is 1.2 factor for bungee jumping',
  })
  amount: number;

  @property({ getType: () => PremiumBreakdown })
  premiumBreakdown: PremiumBreakdown;

  @property({ getType: () => Policy })
  policy: Policy;

  @property({ getType: () => PolicyRider })
  policyRider: PolicyRider;

  @property()
  ruleCode: string;

  @property()
  expiryAfterInYears: number;

  @property({
    comment: 'FlatExtra premium as calculated by the product service. Example is $195 for Heli Skiing',
  })
  premium: number = 0;

  constructor() {
    super();
  }

  static new(
    name: string,
    amount: number,
    policy: Policy,
    premiumBreakdown: PremiumBreakdown,
    ruleCode: string,
    premium: number,
    expiryAfterInYears?: number,
    policyRider?: PolicyRider
  ) {
    return new FlatExtra().init(
      name,
      amount,
      policy,
      premiumBreakdown,
      ruleCode,
      premium,
      expiryAfterInYears,
      policyRider
    );
  }

  init(
    name: string,
    amount: number,
    policy: Policy,
    premiumBreakdown: PremiumBreakdown,
    ruleCode: string,
    premium: number,
    expiryAfterInYears?: number,
    policyRider?: PolicyRider
  ) {
    this.name = name;
    this.amount = amount;
    this.premium = premium;
    this.premiumBreakdown = premiumBreakdown;
    this.policy = policy;
    this.ruleCode = ruleCode;
    this.expiryAfterInYears = expiryAfterInYears;
    this.policyRider = policyRider;
    return this;
  }

  getDescriptionDisplay(language) {
    const displayLanguage = language || 'en';
    const name = this.name || '';
    let availableDescriptions;

    if (this.ruleCode) {
      availableDescriptions = FlatExtraNames[this.ruleCode];
    } else {
      availableDescriptions = {
        en: name.split('; Answered: Yes')[0],
        es: 'El asegurador ha definido un cargo extra plano personalizado',
      };
    }

    return availableDescriptions[displayLanguage];
  }

  calculateMonthlyAmount(): number {
    // Inflight policies will be migrated to have this.premium
    // set
    return Utils.round(this.premium, 2);
  }

  getMonthlyAmount(): number {
    return this.calculateMonthlyAmount();
  }

  getDisplayInfo() {
    return CoverpathUtils.getFlatExtraDisplayInfo(this.name, this.ruleCode, this.expiryAfterInYears);
  }

  clone() {
    return FlatExtra.new(
      this.name,
      this.amount,
      this.policy,
      this.premiumBreakdown,
      this.ruleCode,
      this.premium,
      this.expiryAfterInYears,
      this.policyRider
    );
  }
}
